import { useReducer } from 'react'
import Helper from '../../../helpers/Helper'
import { IFormField, PaduOperation } from '../../../definitions/FormField'

export interface EditorField {
    _id: string
    label: string
    key: string
    type: FieldType
    description?: string
    validation?: {
        isRequired?: boolean
        min?: number
        max?: number
        minLength?: number
        maxLength?: number
        pattern?: string
        allowedValues?: string[]
        fromDate?: string
        toDate?: string
        value?: boolean
    }
    systemConfigType?: string
    stringOptionsType?: string
    isDefault?: boolean
    childFields?: EditorField[]
    objectFields?: EditorField[]
    padu?: {
        preferred?: {
            operation: PaduOperation
            value?: number | string | string[] | { from?: number | string, to?: number | string }
        }
        acceptable?: {
            operation: PaduOperation
            value?: number | string | string[] | { from?: number | string, to?: number | string }
        }
        discouraged?: {
            operation: PaduOperation
            value?: number | string | string[] | { from?: number | string, to?: number | string }
        }
        unacceptable?: {
            operation: PaduOperation
            value?: number | string | string[] | { from?: number | string, to?: number | string }
        }
    }
}

export type FieldType = IFormField['type']

export interface EditorStep {
    _id: string
    label: string
    isDefault?: boolean
    fields: EditorField[]

}

export interface SchemaEditorState {
    label: string
    steps: EditorStep[]
    activeStepId: string | null
    viewMode: 'LIST' | 'EDIT_STEP'
}

export type SchemaEditorAction =
    | { type: 'ADD_STEP' }
    | { type: 'SELECT_STEP', id: string }
    | { type: 'REMOVE_STEP', id: string }
    | { type: 'REORDER_STEPS', steps: EditorStep[] }
    | { type: 'BACK_TO_LIST' }
    | { type: 'OPEN_EDIT' }
    | { type: 'LOAD_SCHEMA', steps: EditorStep[], label: string }
    | { type: 'UPDATE_STEP_LABEL', id: string, label: string }
    | { type: 'ADD_FIELD', stepId: string }
    | { type: 'REMOVE_FIELD', stepId: string, fieldId: string }
    | { type: 'REORDER_FIELDS', stepId: string, fields: EditorField[] }
    | { type: 'UPDATE_FIELD', stepId: string, fieldId: string, updates: Partial<EditorField> }
    | { type: 'ADD_OBJECT_FIELD', fieldId: string }

const initialState: SchemaEditorState = {
    label: '',
    steps: [],
    activeStepId: null,
    viewMode: 'LIST'
}

function removeFieldRecursive(fields: EditorField[], fieldId: string): EditorField[] {
    return fields
        .filter(field => field._id !== fieldId)
        .map(field => ({
            ...field,
            objectFields: field.objectFields
                ? removeFieldRecursive(field.objectFields, fieldId)
                : undefined
        }))
}

function updateFieldRecursive(
    fields: EditorField[],
    fieldId: string,
    updates: Partial<EditorField>
): EditorField[] {
    return fields.map(field => {
        if (field._id === fieldId) {
            return { ...field, ...updates }
        }

        return {
            ...field,
            objectFields: field.objectFields
                ? updateFieldRecursive(field.objectFields, fieldId, updates)
                : field.objectFields,
            childFields: field.childFields
                ? updateFieldRecursive(field.childFields, fieldId, updates)
                : field.childFields
        }
    })
}

function addObjectChildRecursive(fields: EditorField[], parentId: string): EditorField[] {
    return fields.map(field => {
        if (field._id === parentId) {
            const index = (field.objectFields?.length ?? 0) + 1

            const newField: EditorField = {
                _id: Helper.generateUUID(),
                label: 'New Field',
                key: `${field.key}.field_${index}`,
                type: 'string',
                validation: { isRequired: false }
            }

            return {
                ...field,
                objectFields: [...(field.objectFields ?? []), newField]
            }
        }

        return {
            ...field,
            objectFields: field.objectFields
                ? addObjectChildRecursive(field.objectFields, parentId)
                : field.objectFields,
            childFields: field.childFields
                ? addObjectChildRecursive(field.childFields, parentId)
                : field.childFields
        }
    })
}

const schemaEditorReducer = (state: SchemaEditorState, action: SchemaEditorAction): SchemaEditorState => {
    switch (action.type) {
        case 'UPDATE_FIELD':
            return {
                ...state,
                steps: state.steps.map(step =>
                    step._id === action.stepId
                        ? {
                                ...step,
                                fields: updateFieldRecursive(
                                    step.fields,
                                    action.fieldId,
                                    action.updates
                                )
                            }
                        : step
                )
            }

        case 'ADD_STEP': {
            const newStep: EditorStep = {
                _id: Helper.generateUUID(),
                label: `Step ${state.steps.length + 1} `,
                fields: []
            }

            return {
                ...state,
                steps: [...state.steps, newStep]
            }
        }

        case 'SELECT_STEP':
            return {
                ...state,
                activeStepId: action.id
            }

        case 'REMOVE_STEP': {
            const updated = state.steps.filter(s => s._id !== action.id)
            const isRemovingActive = state.activeStepId === action.id

            return {
                label: '',
                steps: updated,
                activeStepId: isRemovingActive ? null : state.activeStepId,
                viewMode: isRemovingActive ? 'LIST' : state.viewMode
            }
        }

        case 'REORDER_STEPS':
            return { ...state, steps: action.steps }

        case 'BACK_TO_LIST':
            return { ...state, viewMode: 'LIST' }

        case 'OPEN_EDIT':
            return {
                ...state,
                viewMode: 'EDIT_STEP'
            }

        case 'LOAD_SCHEMA':
            return {
                label: action.label,
                steps: action.steps,
                activeStepId: null,
                viewMode: 'LIST'
            }

        case 'UPDATE_STEP_LABEL':
            return {
                ...state,
                steps: state.steps.map(step => step._id === action.id ? { ...step, label: action.label } : step)

            }

        case 'ADD_FIELD':
            return {
                ...state,
                steps: state.steps.map(step =>
                    step._id === action.stepId
                        ? {
                                ...step,
                                fields: [
                                    ...step.fields,
                                    {
                                        _id: Helper.generateUUID(),
                                        label: `Field ${step.fields.length + 1}`,
                                        key: `field_${step.fields.length + 1}`,
                                        type: 'string',
                                        validation: {
                                            isRequired: false
                                        }

                                    }
                                ]
                            }
                        : step
                )
            }

        case 'REMOVE_FIELD':
            return {
                ...state,
                steps: state.steps.map(step =>
                    step._id === action.stepId
                        ? {
                                ...step,
                                fields: removeFieldRecursive(step.fields, action.fieldId)
                            }
                        : step
                )
            }

        case 'REORDER_FIELDS':
            return {
                ...state,
                steps: state.steps.map(step =>
                    step._id === action.stepId ? { ...step, fields: action.fields } : step
                )
            }

        case 'ADD_OBJECT_FIELD':
            return {
                ...state,
                steps: state.steps.map(step => ({
                    ...step,
                    fields: addObjectChildRecursive(step.fields, action.fieldId)
                }))
            }

        default: return state
    }
}

export const useSchemaSteps = () =>
    useReducer(schemaEditorReducer, initialState)
